import { ChangeEvent, useCallback, useRef } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import styles from "./DatePicker.module.css";
import CalendarIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CalendarIconPath";
import { FixaFormField } from "@coworker/apprestructured/src/shared/wrappers/FixaFormField/FixaFormField";
import { getDateFormatsForLocale, resolveLocale } from "./helper";
dayjs.extend(customParseFormat);

type DatePickerProps = {
  label?: string | undefined;
  // Null means no date is selected
  value: string | null;
  // Will be called with null if no date is selected
  onChange: (value: string | null) => void;
  errorMessage?: string | null;
  min?: string | undefined;
  max?: string | undefined;
  testId?: string;
};

export const DatePicker = ({
  label,
  value,
  onChange,
  errorMessage,
  min,
  max,
  testId,
}: DatePickerProps) => {
  const nativeInputRef = useRef<HTMLInputElement>(null);

  const handleOnClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    const input = nativeInputRef.current;
    if (!input) return;

    input.focus();
    input.showPicker();
  };

  const locale = resolveLocale();
  const dateFormatsForLocale = getDateFormatsForLocale(locale);
  const inputFieldValue = value
    ? dayjs(value, "YYYY-MM-DD", true).format(dateFormatsForLocale.full)
    : dateFormatsForLocale.full;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value ?? null);
    },
    [onChange]
  );

  return (
    <div className={styles["root"]}>
      <input
        data-testid={testId ?? "date-picker"}
        ref={nativeInputRef}
        type="date"
        value={value ?? ""}
        onChange={handleChange}
        tabIndex={-1}
        className={styles["nativeInput"]}
        min={min}
        max={max}
      />
      <FixaFormField
        valid={!errorMessage}
        shouldValidate={!!errorMessage}
        validation={{ id: "datePicker", msg: errorMessage, type: "error" }}
        className={styles["formField"]}
      >
        <FixaInputField
          id="dateInput"
          type="text"
          value={inputFieldValue}
          ssrIcon={CalendarIconPath}
          iconPosition="trailing"
          iconOnClick={handleOnClick}
          onClick={handleOnClick}
          label={label}
        />
      </FixaFormField>
    </div>
  );
};
